// src/Api.ts

const BASE_URL = 'https://api.somukoi.com/api';

// Storage Keys
const ACCESS_TOKEN_KEY = 'auth_access_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const USER_KEY = 'auth_user';

// Token & User Management Helpers
export const getAccessToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const setTokens = (accessToken: string, refreshToken?: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
};

export const getStoredUser = (): UserProfile | null => {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

export const setStoredUser = (user: UserProfile | RegisteredUser): void => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const clearAuth = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// Types
export interface UserProfile {
  id: number;
  email: string | null;
  password?: string;
  firebase_uid?: string | null;
  avatar?: string | null;
  role: string;
  name: string;
  phoneNumber: string;
  isVerfied?: boolean;
  profilePictureUrl?: string | null;
  points?: number;
  demographic_info?: any;
  disabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface RegisteredUser {
  id: number;
  email: string | null;
  role: string;
  name: string;
  phoneNumber: string;
}

export interface RegisterPayload {
  name: string;
  password: string;
  role: string;
  phoneNumber: string;
}

export interface RegisterResponse {
  message: string;
  user: RegisteredUser;
  token?: string;
}

export interface LoginPayload {
  phoneNumber: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  accessToken: string;
  refreshToken: string;
  user: UserProfile;
}

export interface RefreshTokenPayload {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  accessToken: string;
  refreshToken: string;
}

export interface Category {
  id: number;
  name: string;
  description: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CategoriesResponse {
  message: string;
  categories: Category[];
}

export interface CreateBlogPayload {
  title: string;
  content: string;
  media_type: 'text' | 'video' | 'image';
  media_url?: string;
  location: string;
  category_id: number;
  tags?: string[];
  status?: string;
  cover_image_url?: string | null;
}

export interface BlogPost {
  id: number;
  title: string;
  content: string;
  media_url: string;
  media_type: string;
  location: string;
  category_id: number;
  cover_image_url?: string | null;
  slug: string;
  author_id: number;
  status: string;
  tags: string[];
  createdAt?: string;
  updatedAt?: string;
  author?: {
    id: number;
    name: string;
    avatar?: string | null;
    profilePictureUrl?: string | null;
  };
  category?: {
    id: number;
    name: string;
  };
}

export interface CreateBlogResponse {
  success: boolean;
  blog: BlogPost;
  message: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Universal helper to attach dynamic Authorization headers
 */
export const getAuthHeaders = (): HeadersInit => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

/**
 * Register User API
 * POST https://api.somukoi.com/api/auth/register
 */
export const registerUser = async (
  payload: RegisterPayload
): Promise<ApiResponse<RegisterResponse>> => {
  try {
    const response = await fetch(`${BASE_URL}/auth/register`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || `Error ${response.status}: Failed to register user`,
      };
    }

    if (data.token) {
      setTokens(data.token);
    }
    if (data.user) {
      setStoredUser(data.user);
    }

    return {
      success: true,
      data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Network error occurred during registration',
    };
  }
};

/**
 * Login User API
 * POST https://api.somukoi.com/api/auth/login
 */
export const loginUser = async (
  payload: LoginPayload
): Promise<ApiResponse<LoginResponse>> => {
  try {
    const response = await fetch(`${BASE_URL}/auth/login`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok || data.success === false) {
      return {
        success: false,
        error: data.message || `Error ${response.status}: Failed to login`,
      };
    }

    if (data.accessToken) {
      setTokens(data.accessToken, data.refreshToken);
    }
    if (data.user) {
      setStoredUser(data.user);
    }

    return {
      success: true,
      data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Network error occurred during login',
    };
  }
};

/**
 * Refresh the current session tokens
 * POST https://api.somukoi.com/api/auth/refreshtoken
 */
export const refreshAccessToken = async (
  payload?: Partial<RefreshTokenPayload>
): Promise<ApiResponse<RefreshTokenResponse>> => {
  const refreshToken = payload?.refreshToken || getRefreshToken();

  if (!refreshToken) {
    return {
      success: false,
      error: 'No refresh token is available',
    };
  }

  try {
    const response = await fetch(`${BASE_URL}/auth/refreshtoken`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ refreshToken }),
    });

    const data = await response.json();

    if (!response.ok || data.success === false) {
      return {
        success: false,
        error: data.message || `Error ${response.status}: Failed to refresh token`,
      };
    }

    if (data.accessToken && data.refreshToken) {
      setTokens(data.accessToken, data.refreshToken);
    }

    return {
      success: true,
      data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Network error occurred while refreshing token',
    };
  }
};

/**
 * Get All Categories API (Authenticated)
 * GET https://api.somukoi.com/api/category/allcategories
 */
export const getAllCategories = async (): Promise<ApiResponse<CategoriesResponse>> => {
  try {
    const response = await fetch(`${BASE_URL}/category/allcategories`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || `Error ${response.status}: Failed to fetch categories`,
      };
    }

    return {
      success: true,
      data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Network error occurred while fetching categories',
    };
  }
};

/**
 * Create Blog Post API (Authenticated)
 * POST https://api.somukoi.com/api/blog/create
 */
export const createBlogPost = async (
  payload: CreateBlogPayload
): Promise<ApiResponse<CreateBlogResponse>> => {
  try {
    const response = await fetch(`${BASE_URL}/blog/create`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok || data.success === false) {
      return {
        success: false,
        error: data.message || `Error ${response.status}: Failed to create blog post`,
      };
    }

    return {
      success: true,
      data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Network error occurred while creating blog post',
    };
  }
};